import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../config/axiosConfig";
import { useSelector } from "react-redux";
import { useMemo } from "react";

export const fetchComprobantes = async (id_empresa) => {
  if (!id_empresa) {
    console.warn("⚠️ `id_empresa` no es válido en fetchTerceros:", id_empresa);
    return [];
  }

  try {
    const { data } = await api.get(`/${id_empresa}/comprobantes`);
    return data;
  } catch (error) {
    console.error("❌ Error en la petición:", error);
    return [];
  }
};

export const useComprobantes = () => {
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id; // ✅ Asegurar que existe un `id_empresa`

  return useQuery({
    queryKey: ["comprobantes", id_empresa], // Clave para el caché
    queryFn: () => (id_empresa ? fetchComprobantes(id_empresa) : Promise.resolve([])),
    enabled: !!id_empresa,
  });
};

export const useUpdateComprobante = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;
  return (nuevoComprobante) => {
    if (!id_empresa) throw new Error("⚠️ `id_empresa` es obligatorio.");
    if (!nuevoComprobante?.id) throw new Error("⚠️ `id` del comprobante es obligatorio.");
    queryClient.setQueryData(["comprobantes", id_empresa], (oldData) => {
      if (!oldData) return [nuevoComprobante]; // Si no hay datos previos, inicializa con el nuevo comprobante
      const exists = oldData.some((comp) => String(comp.id) === String(nuevoComprobante.id));

      if (exists) {
        return oldData.map((comp) => (String(comp.id) === String(nuevoComprobante.id) ? nuevoComprobante : comp));
      }

      return [nuevoComprobante, ...oldData]; // Agregar el nuevo comprobante a la lista
    });
  };
};

export const useComprobanteById = (id_comprobante) => {
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useQuery({
    queryKey: ["comprobante", id_empresa, id_comprobante],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/${id_empresa}/comprobantes/${id_comprobante}`);
        return data;
      } catch (error) {
        console.error("❌ Error al obtener comprobante:", error);
        throw new Error("No se pudo obtener el comprobante");
      }
    },
    staleTime: 30000, // Cachea los datos por 60s para evitar múltiples llamadas innecesarias
    refetchOnWindowFocus: false, // No recarga automáticamente al enfocar la ventana
    enabled: !!id_empresa && !!id_comprobante,
  });
};

export const useComprobantesByIds = (ids = []) => {
  const { data: comprobantes, isLoading, isError, error } = useComprobantes();

  const data = useMemo(() => {
    if (!comprobantes || ids.length === 0) return [];
    return comprobantes.filter((comp) => ids.includes(comp.id));
  }, [comprobantes, ids]);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

export const useDeleteComprobante = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  const mutation = useMutation({
    mutationFn: async (id_comprobante) => {
      if (!id_empresa) throw new Error("⚠️ `id_empresa` es obligatorio.");
      if (!id_comprobante) throw new Error("⚠️ `id_comprobante` es obligatorio.");

      await api.delete(`/${id_empresa}/comprobantes/${id_comprobante}`);
      return id_comprobante;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(["comprobantes", id_empresa], (oldData) => {
        if (!oldData) return [];
        const newData = oldData.filter((c) => String(c.id) !== String(deletedId));
        return [...newData];
      });
    },
  });

  return mutation;
};
